/** Max edge length after resize — enough for map profiles, small enough for Workers/nginx. */
const MAX_EDGE_PX = 1600
/** Target quality for JPEG output. */
const JPEG_QUALITY = 0.82
/** Soft ceiling after compression; we step quality down if needed.
 * Keep under nginx’s common 1m default so uploads work before/without a proxy bump. */
const TARGET_MAX_BYTES = 900_000

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image'))
    }
    image.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Could not encode image'))
        else resolve(blob)
      },
      type,
      quality,
    )
  })
}

/**
 * Resize and re-encode a photo in the browser so uploads stay under proxy/Worker limits.
 * Always returns a JPEG File suitable for /api/images.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are accepted')
  }

  const image = await loadImage(file)
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(image.width, image.height))
  const width = Math.max(1, Math.round(image.width * scale))
  const height = Math.max(1, Math.round(image.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not prepare image')
  context.drawImage(image, 0, 0, width, height)

  let quality = JPEG_QUALITY
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality)
  while (blob.size > TARGET_MAX_BYTES && quality > 0.45) {
    quality -= 0.08
    blob = await canvasToBlob(canvas, 'image/jpeg', quality)
  }

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo'
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  })
}

export async function prepareImagesForUpload(files: File[]): Promise<File[]> {
  const prepared: File[] = []
  for (const file of files) {
    prepared.push(await prepareImageForUpload(file))
  }
  return prepared
}
